import React, { useEffect, useRef, useState } from 'react'
import { Button } from '../../../Button'
import { cn } from '../../../../../lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '../../../Popover'
import { Icon, iconTypes } from '../../../Icon'
import { Chip } from '../../../Chip'

type Action = {
  id: string
  label: string
  description: string
}

export interface ChatInputBarProps {
  title?: string
  placeholder: string
  handleChange: (message: string) => void | Promise<void>
  handleSend: (message: string) => void | Promise<void>
  handleTriggerAction: (
    actionId: string,
    prompt: string,
  ) => void | Promise<void>
  isDisabled?: boolean
  isLoading?: boolean
  value: string
  resetKey?: number
  popover?: {
    label: string
    truncatedLabel: string
    value: string
    icon?: iconTypes
    options: {
      label: string
      value: string
      description?: string
      icon?: iconTypes
      selected?: boolean
    }[]
    handleChange: (value: string) => void | Promise<void>
  }
  actions: Action[]
  onActionSelect?: (action: Action) => void | Promise<void>
  selectedAction?: {
    id: string
    label: string
    description: string
  } | null
  onSetSelectedAction?: (
    action: {
      id: string
      label: string
      description: string
    } | null,
  ) => void | Promise<void>
}

export default function ChatInputBar(props: ChatInputBarProps) {
  const {
    value,
    handleChange,
    handleSend,
    handleTriggerAction,
    actions,
    onActionSelect,
    isDisabled,
    title,
    isLoading,
    placeholder,
    popover,
    resetKey,
    selectedAction,
    onSetSelectedAction,
  } = props

  const [inputValue, setInputValue] = useState<string>(value || '')
  const [lastChange, setLastChange] = useState<string>(value || '')
  const [filteredActions, setFilteredActions] =
    useState<ChatInputBarProps['actions']>(actions)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [openActions, setOpenActions] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [labelText, setLabelText] = useState('')
  const [open, setOpen] = useState(false)
  const [popoverLabel, setPopoverLabel] = useState(popover?.label || '')
  const [isChipDeleteMode, setIsChipDeleteMode] = useState(false)

  // Reset selectedAction when resetKey changes
  useEffect(() => {
    if (resetKey !== undefined) {
      onSetSelectedAction?.(null)
      setIsChipDeleteMode(false)
    }
  }, [resetKey, onSetSelectedAction])

  const typingSpeed = 30 // Adjust typing speed in milliseconds

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  let timer: NodeJS.Timeout

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms))

  const hasChangedFromOutside = (value: string | number | undefined) => {
    return (
      lastChange == '' ||
      (lastChange == inputValue && inputValue != value && value !== undefined)
    )
  }

  const typeToTarget = async (goal: string, target: string) => {
    if (goal.length == target.length) {
      return goal
    }

    if (goal.length > target.length) {
      // loop through and decrement the last character

      while (goal.length > target.length) {
        goal = goal.slice(0, -1)
        setLabelText(goal)
        await sleep(typingSpeed)
      }
    }

    if (goal.length < target.length) {
      // loop through and increment the last character

      while (goal.length < target.length) {
        goal += target[goal.length]
        setLabelText(goal)
        await sleep(typingSpeed)
      }
    }
  }

  useEffect(() => {
    if (popoverLabel != popover?.label && !isFocused) {
      typeToTarget('', popover?.truncatedLabel || '')
      setPopoverLabel(popover?.label || '')
      return
    }

    if (isFocused) {
      setLabelText('')
      return
    }

    if (!isFocused) {
      typeToTarget(labelText, popover?.truncatedLabel || '')
      return
    }
  }, [inputValue, isFocused, popover])

  // first render
  useEffect(() => {
    if (!inputValue && value) {
      setLastChange(value)
      return setInputValue(value)
    }

    // if value changes from outside, update input value
    if (hasChangedFromOutside(value) && value !== undefined) {
      setLastChange(value)
      return setInputValue(value)
    }
  }, [value])

  // Adjust the height of the textarea to fit the content
  useEffect(() => {
    const textarea = textareaRef.current

    if (!textarea) {
      return
    }

    textarea.style.height = '36px'
    textarea.style.height = `${textarea.scrollHeight}px`
  }, [inputValue])

  useEffect(() => {
    if (inputValue[0] == '/' && !isLoading) {
      const searchValue = inputValue.slice(1)
      setOpenActions(true)

      if (inputValue.length == 1) return setFilteredActions(actions)
      setFilteredActions(
        actions.filter((action) =>
          action.label.toLowerCase().includes(searchValue.toLowerCase()),
        ),
      )
      setFocusedIndex(-1)
    }
    if (inputValue[0] != '/') {
      setOpenActions(false)
      setFocusedIndex(-1)
    }
    if (isLoading) {
      setInputValue('')
    }
  }, [inputValue])

  // debounce function calling timeout
  const debounce = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      clearTimeout(timer)
      setInputValue(e.target.value)
      timer = setTimeout(() => {
        triggerChange(e)
        setLastChange(e.target.value)
      }, 0)
    },
    [],
  )

  const triggerChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // dispatch if exists
    handleChange && handleChange(e.target.value)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle backspace on empty textarea with selected action
    if (event.key === 'Backspace' && inputValue === '' && selectedAction) {
      event.preventDefault()
      if (isChipDeleteMode) {
        // Delete the chip
        onSetSelectedAction?.(null)
        setIsChipDeleteMode(false)
      } else {
        // Enable delete mode
        setIsChipDeleteMode(true)
      }
      return
    }

    // Reset delete mode when typing
    if (event.key !== 'Backspace' && isChipDeleteMode) {
      setIsChipDeleteMode(false)
    }

    if (openActions && filteredActions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setFocusedIndex((prevIndex) =>
          prevIndex < filteredActions.length - 1 ? prevIndex + 1 : 0,
        )
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setFocusedIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : filteredActions.length - 1,
        )
      } else if (event.key === 'Enter') {
        event.preventDefault()
        handleKeyEnter(event)
      }
    }
    if (event.key === 'Enter' && !openActions) {
      event.preventDefault()
      handleKeyEnter(event)
    }
    if (event.key === 'Escape') {
      if (openActions) {
        setInputValue('')
        setOpenActions(false)
      }
      if (isChipDeleteMode) {
        setIsChipDeleteMode(false)
      }
    }
  }

  const handleKeyEnter = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (openActions && filteredActions.length > 0) {
      // execute selected action when actions is opened with enter key.
      event.preventDefault()
      const actionToSelect =
        focusedIndex >= 0 ? filteredActions[focusedIndex] : filteredActions[0] // Use first action if none is focused

      if (actionToSelect) {
        onSetSelectedAction?.(actionToSelect)
        onActionSelect?.(actionToSelect)
        setFocusedIndex(-1)
        setInputValue('')
      }
    } else if (event.shiftKey) {
      // If Shift + Enter, create a new line
      handleChange(value + '\n')
    } else {
      event.preventDefault()

      // only trigger action if selected action is not null
      if (selectedAction) {
        setInputValue('')
        return handleTriggerAction(selectedAction.id, value)
      }

      if (value && value[0] !== '/') {
        handleSend(value)
        setInputValue('')
      }
    }
  }

  return (
    <div className="p-2 flex justify-between w-full items-center gap-2 relative">
      {openActions && (
        <div
          style={{ backdropFilter: 'blur(10px)' }}
          className={cn(
            'w-[calc(100%-3.75rem)] max-h-[200px] border rounded-lg flex flex-col p-2 overflow-y-auto absolute bottom-14 bg-primary-foreground/70',
          )}
        >
          {filteredActions.length > 0 ? (
            filteredActions.map((action, index) => (
              <Button
                isDisabled={isDisabled}
                key={index}
                handleClick={() => {
                  setInputValue('')
                  onSetSelectedAction?.(action)

                  if (selectedAction) {
                    onActionSelect?.(selectedAction)
                  }
                }}
                className={cn(
                  'min-h-[48px]',
                  focusedIndex === index ? 'bg-accent' : '',
                )}
                variant={'ghost'}
                size={'action'}
                label={action.label}
                leftIcon="Zap"
                contentPosition="left"
                description={action.description}
              />
            ))
          ) : (
            <div className="text-center h-5 text-sm">No actions found</div>
          )}
        </div>
      )}
      <div
        className={cn(
          'w-full rounded-lg max-h-36 bg-background border shadow-sm text-sm flex items-center focus-within:ring-1 focus-within:ring-ring',
          {
            'animate-pulse': isLoading,
          },
        )}
      >
        {/* channel selector */}
        <div className="flex items-center gap-2">
          <Popover
            open={open}
            onOpenChange={(isOpen) => {
              setIsFocused(!isOpen)
              setOpen(isOpen)
            }}
          >
            <PopoverTrigger>
              <div className="flex items-center gap-2 pl-3 pr-2 py-1 text-muted-foreground max-w-[200px] md:max-w-[260px] lg:max-w-[320px]">
                {popover && !popover?.icon && (
                  <Icon name="LoaderCircle" className="mr-2 animate-spin" />
                )}
                {popover?.icon && (
                  <Icon name={popover?.icon} className="shrink-0" />
                )}
                <span className="truncate block whitespace-nowrap min-w-0">
                  {labelText}
                </span>
                {popover?.icon && (
                  <Icon name={'ChevronRight'} className="shrink-0" />
                )}
              </div>
            </PopoverTrigger>
            <PopoverContent side="top" align="start" className="p-0">
              <div className="bg-primary-foreground rounded-lg">
                {popover?.options.map((option, index) => (
                  <Button
                    key={index}
                    handleClick={async () => {
                      await popover.handleChange(option.value)
                      setOpen(false)
                    }}
                    className="w-full justify-between"
                    variant="ghost"
                    size="action"
                    label={option.label}
                    leftIcon={option.icon || 'RadioTower'}
                    rightIcon={option.selected ? 'Check' : undefined}
                    contentPosition="left"
                    description={option.description}
                  />
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* selected action chip */}
        {selectedAction && (
          <div className="flex items-center gap-2 mr-2">
            <Chip
              label={selectedAction.label}
              variant="primary"
              className="flex-shrink-0"
              deleteModeEnabled={true}
              isDeleteMode={isChipDeleteMode}
              onDeleteModeChange={setIsChipDeleteMode}
              handleDelete={() => {
                onSetSelectedAction?.(null)
                setIsChipDeleteMode(false)
              }}
            />
          </div>
        )}

        <textarea
          title={`${title}-input`}
          disabled={isDisabled}
          ref={textareaRef}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={isLoading ? 'Generating message...' : placeholder}
          value={inputValue}
          onKeyDown={handleKeyDown}
          onChange={debounce}
          className="w-full py-2 max-h-36 focus-within:outline-none flex items-center resize-none overflow-y-auto bg-transparent text-base"
        />
      </div>
      <Button
        title={`${title}-send-message`}
        isDisabled={inputValue.trim() === '' || isDisabled}
        handleClick={() => {
          if (selectedAction) {
            return handleTriggerAction(selectedAction.id, inputValue)
          }

          if (inputValue && inputValue[0] !== '/') {
            handleSend(inputValue)
            setInputValue('')
          }
        }}
        variant={'ghost'}
        size={'icon'}
        contentPosition="center"
        className={cn(
          'h-9 w-9',
          'rounded-full text-muted-foreground bg-muted shrink-0',
          {
            'bg-message-background hover:bg-message-background text-white opacity-80 hover:opacity-100 hover:text-white':
              inputValue,
          },
        )}
        leftIcon="Send"
      />
    </div>
  )
}
